import React, { useLayoutEffect, useRef } from "react";
import { useOutletContext, useSearchParams, useNavigationType, Link } from "react-router-dom";
import { IconArrowNarrowRight } from '@tabler/icons-react';
import ShopCard from "./ShopCard";
import TextTabs, { tabId } from '../../../Components/Globals/TextTabs';
import { byShopOrder, shopTypes } from '../Shop_Functions/catalog';

const ALL = 'all';
const scrollKey = (search) => `shop-scroll:${search}`;

// Whether this page load has shown the shop before, so a later 'POP' is a real Back/Forward.
// React Router also reports the first render of a page load as 'POP'.
let shownThisPageLoad = false;
// A page load that itself came from Back/Forward (e.g. after a full-page navbar link)
const pageLoadIsBackForward = () => {
    try { return performance.getEntriesByType('navigation')[0]?.type === 'back_forward'; } catch (err) { return false; }
};

const ShopDisplay = () => {
    const { items } = useOutletContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigationType = useNavigationType();

    const types = shopTypes(items);
    const requested = searchParams.get('type');
    const type = types.some((t) => t.value === requested) ? requested : ALL;
    const tabs = [{ value: ALL, label: 'All', count: items.length }, ...types];
    const pieces = items.filter((item) => type === ALL || item.product_type === type).slice().sort(byShopOrder);

    // Coming back from a piece (browser back) returns to the same spot in the same filter;
    // arriving any other way (a link) starts at the top. Card photos have a fixed shape, so
    // the grid's height is known before images load.
    const search = searchParams.toString();
    // Filters change without remounting, so read the current one when leaving
    const searchRef = useRef(search);
    searchRef.current = search;
    useLayoutEffect(() => {
        const isBackForward = navigationType === 'POP' && (shownThisPageLoad || pageLoadIsBackForward());
        shownThisPageLoad = true;
        if (isBackForward) {
            let saved = null;
            try { saved = sessionStorage.getItem(scrollKey(searchRef.current)); } catch (err) { /* storage unavailable */ }
            if (saved !== null) window.scrollTo(0, Number(saved));
        } else {
            window.scrollTo(0, 0);
        }
        const savePosition = () => {
            try { sessionStorage.setItem(scrollKey(searchRef.current), String(window.scrollY)); } catch (err) { /* storage unavailable */ }
        };
        // Full-page navigations (e.g. the navbar's links) leave without unmounting, so save on pagehide too
        window.addEventListener('pagehide', savePosition);
        return () => {
            window.removeEventListener('pagehide', savePosition);
            savePosition();
        };
        // Restore once on arrival; save on leaving
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The filter lives in the URL so it can be bookmarked and linked to ("Shop / Bowls")
    const selectType = (value) => {
        setSearchParams(value === ALL ? {} : { type: value }, { replace: true });
    };

    return (
        <div className='flex w-full pb-16 justify-center'>
            <div className="w-full min-w-0 flex flex-col max-w-[1100px] px-4">
                <div className='text-white pt-8 pb-3 text-2xl font-light uppercase'>
                    Shop
                </div>
                <p className='m-0 mb-8 text-zinc-400 leading-relaxed'>
                    Hand-blown glass by Juli Juneau, New Orleans. Ask about any piece for pricing and shipping.
                </p>

                <TextTabs
                    tabs={tabs}
                    value={type}
                    onChange={selectType}
                    label='Piece type'
                    idPrefix='shop'
                    className='mb-8'
                />

                <div id='shop-panel' role='tabpanel' aria-labelledby={tabId('shop', type)}>
                    {pieces.length > 0
                        // 3 columns on desktop, 2 on tablets and phones
                        ? <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-2 gap-6 sm:gap-3">
                            {pieces.map((row) => (
                                <ShopCard key={row.id} item={row} to={`${row.id}`} />
                            ))}
                        </div>
                        : <div className='flex flex-col items-start gap-3 py-8'>
                            <div className='text-zinc-300'>
                                {type === ALL ? 'There are no pieces in the shop right now.' : 'No pieces in this category right now.'}
                            </div>
                            {type !== ALL &&
                                <button
                                    type='button'
                                    onClick={() => selectType(ALL)}
                                    style={{ fontFamily: 'inherit' }}
                                    className='p-0 bg-transparent border-0 cursor-pointer text-white underline underline-offset-4'
                                >
                                    See all pieces
                                </button>
                            }
                        </div>
                    }
                </div>

                <section className='mt-16 pt-10 border-solid border-0 border-t border-zinc-800 flex flex-col items-start gap-3'>
                    <h2 className='m-0 text-white text-xl font-light uppercase tracking-wide'>Looking for something specific?</h2>
                    <p className='m-0 text-zinc-400 leading-relaxed'>
                        Juli makes custom pieces, awards and memorials.
                    </p>
                    <Link to='/contact' className='inline-flex items-center gap-1 text-white underline underline-offset-4 hover:text-zinc-300'>
                        Get in touch
                        <IconArrowNarrowRight size={18} stroke={1.75} aria-hidden='true' />
                    </Link>
                </section>
            </div>
        </div>
    )
}

export default ShopDisplay
